import React, { useState } from "react";
import toast from "react-hot-toast";
import { ArrowUpRight } from "lucide-react";
import { api, ApiError } from "../lib/api";
import Footer from "../components/Layout/Footer";
import { PublicHeader } from "../components/Layout/PublicHeader";
import DividerMotif from "../components/Brand/DividerMotif";
import { useDocumentMeta } from "../lib/useDocumentMeta";

/*
 * Velaigal /contact page.
 *
 * Distinctive move: the channels ARE the design. Each contact value
 * (im.vmanoj2002@gmail.com, +91 8220 243 513, etc.) renders as Fraunces 36-72px
 * editorial typography, like a magazine masthead. The contact info is the
 * visual subject of the page, not the form. The form sits below as a
 * second beat, properly framed but quieter.
 */

interface Channel {
  label: string;
  value: string;
  href?: string;
  caption: string;
}

const CHANNELS: Channel[] = [
  {
    label: "Email",
    value: "im.vmanoj2002@gmail.com",
    href: "mailto:im.vmanoj2002@gmail.com",
    caption: "Replies usually inside one business day.",
  },
  {
    label: "Phone",
    value: "+91 8220 243 513",
    href: "tel:+918220243513",
    caption: "Monday to Friday, 10am to 7pm IST.",
  },
  {
    label: "WhatsApp",
    value: "wa.me/918220243513",
    href: "https://wa.me/918220243513",
    caption: "For pilots and quick questions.",
  },
];

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useDocumentMeta(
    "Contact · Velaigal",
    "Talk to a human at Velaigal — email, phone, WhatsApp, or the contact form. HR leader, candidate, investor, or press, same inbox."
  );

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await api("/api/contact", {
        method: "POST",
        body: {
          name: formData.name,
          email: formData.email,
          message: `[${formData.subject}] ${formData.message}`,
        },
      });
      toast.success("Message sent. We'll get back within a business day.");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      const err = error instanceof ApiError ? error : new Error("Something went wrong");
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-cream text-ink">
      <PublicHeader />

      {/* ---------------- Hero ---------------- */}
      <section className="max-w-7xl mx-auto px-8 pt-20 pb-12">
        <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
          Contact
        </span>
        <h1
          className="font-display text-5xl lg:text-6xl font-medium text-ink leading-tight tracking-tight mt-6 max-w-4xl"
          style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
        >
          Talk to a human.
        </h1>
        <span aria-hidden="true" className="block w-12 h-px bg-saffron mt-8" />
        <p className="mt-8 text-lg text-ink-secondary max-w-2xl leading-relaxed">
          HR leader, candidate, investor, journalist. Same inbox. We answer.
        </p>
      </section>

      <DividerMotif className="py-2" />

      {/* ---------------- Channels as huge typography ----------------
       * The distinctive move: each direct line is rendered as editorial
       * typography. The contact info IS the page. Form sits below as a
       * second beat. */}
      <section>
        <div className="max-w-7xl mx-auto px-8 pt-20 pb-24">
          <div className="flex items-baseline justify-between flex-wrap gap-6 mb-16">
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              Direct lines
            </span>
            <span className="text-sm text-ink-tertiary">
              Pick whichever feels right. They all reach a real person.
            </span>
          </div>

          <ul className="divide-y divide-hairline">
            {CHANNELS.map((channel) => {
              const isExternal = channel.href?.startsWith("http");
              const ValueElement = channel.href ? "a" : "span";
              const valueProps = channel.href
                ? {
                    href: channel.href,
                    target: isExternal ? "_blank" : undefined,
                    rel: isExternal ? "noopener noreferrer" : undefined,
                  }
                : {};

              return (
                <li
                  key={channel.label}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-4 py-10 lg:py-12 group"
                >
                  <div className="lg:col-span-2">
                    <span className="text-[11px] uppercase tracking-widest text-hairline-strong font-medium">
                      {channel.label}
                    </span>
                  </div>

                  <div className="lg:col-span-7">
                    <ValueElement
                      {...valueProps}
                      className={
                        channel.href
                          ? "font-display font-medium text-ink hover:text-saffron transition-colors no-underline inline-flex items-baseline gap-3 leading-[1.05] velaigal-headline-hover"
                          : "font-display font-medium text-ink inline-flex items-baseline gap-3 leading-[1.05]"
                      }
                      style={{
                        fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0',
                        fontSize: "clamp(2.25rem, 5.5vw, 4rem)",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {channel.value}
                      {isExternal && (
                        <ArrowUpRight
                          className="h-6 w-6 text-ink-tertiary group-hover:text-saffron transition-colors flex-shrink-0"
                          strokeWidth={1.5}
                          aria-hidden="true"
                        />
                      )}
                    </ValueElement>
                  </div>

                  <div className="lg:col-span-3 lg:pt-3">
                    <p className="text-sm text-ink-tertiary leading-relaxed">
                      {channel.caption}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <DividerMotif className="py-2" />

      {/* ---------------- Form as the second beat ---------------- */}
      <section>
        <div className="max-w-7xl mx-auto px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              Or write to us
            </span>
            <h2
              className="font-display text-3xl lg:text-4xl font-medium text-ink leading-tight tracking-tight mt-6 velaigal-headline-hover"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
            >
              A few sentences is enough.
            </h2>
            <p className="mt-6 text-base text-ink-secondary leading-relaxed max-w-sm">
              Tell us who you are, what you are working on, and what would make
              this conversation worth your time.
            </p>

            <div className="mt-12 pt-8 border-t border-hairline">
              <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium block mb-3">
                Response time
              </span>
              <p className="text-sm text-ink-secondary leading-relaxed">
                One business day, usually faster. We are a small team that
                still reads its own inbox.
              </p>
            </div>
          </div>

          <div className="lg:col-span-8">
            <form onSubmit={handleSubmit} className="space-y-6 max-w-xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-ink"
                  >
                    Your name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your full name"
                    className="input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-ink"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="priya@company.com"
                    className="input"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-ink"
                >
                  Subject
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder="Pilot programme enquiry"
                  className="input"
                  required
                  maxLength={200}
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-ink"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us a little about your team and what you are trying to solve."
                  className="input resize-y"
                  required
                  maxLength={4500}
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full sm:w-auto"
                >
                  {isSubmitting ? "Sending" : "Send message"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
